/**
 * Turning the dial so the selection faces the cards (KEY-532).
 *
 * The dial is authored from 12 o'clock, sweeping clockwise, with each section taking an equal slice and each
 * action point an equal share of its section's. In Dynamic mode the selection is brought round to EAST — 3
 * o'clock, the side the cards are on — so what you picked is pointing at what you are reading.
 *
 * Pure, because the ordering rule IS the feature: get the shortest-way arithmetic wrong and the dial unwinds
 * three-quarters of a turn to reach a neighbour, which reads as a fault rather than as motion.
 */

/** A section as the rotation cares about it: its key and how many action points it holds. */
export interface RotatableSection {
  key: string;
  segments: { key: string }[];
}

/** Clockwise degrees from 12 o'clock to the centre of a selection, or null when it isn't on the dial. */
export function midDegreesOf(
  sections: readonly RotatableSection[],
  sectionKey: string | null | undefined,
  segmentKey?: string | null,
): number | null {
  if (!sectionKey || sections.length === 0) {
    return null;
  }
  const index = sections.findIndex((s) => s.key === sectionKey);
  if (index < 0) {
    return null;
  }

  const perSection = 360 / sections.length;
  if (!segmentKey) {
    return (index + 0.5) * perSection;
  }

  const segments = sections[index].segments;
  const segmentIndex = segments.findIndex((sg) => sg.key === segmentKey);
  if (segmentIndex < 0) {
    // A section that IS on the dial with an action point that isn't: turn to the section rather than
    // refusing to move, which is the nearer truth of the two.
    return (index + 0.5) * perSection;
  }

  const perSegment = perSection / segments.length;
  return index * perSection + (segmentIndex + 0.5) * perSegment;
}

/**
 * The cumulative rotation that brings `midDeg` to East, taken the SHORT way from `current`.
 *
 * Cumulative is the load-bearing word. Returning an absolute angle in [0, 360) would make the step from 350°
 * to 10° a 340° unwind; normalising the DELTA to (−180, 180] and adding it keeps every move the short one,
 * however many turns have accumulated.
 */
export function rotationToEast(midDeg: number, current: number): number {
  const delta = 90 - midDeg - (((current % 360) + 360) % 360);
  return current + ((((delta + 180) % 360) + 360) % 360) - 180;
}

/** Whether a move is big enough to be worth animating — and worth hiding the cards for. */
export function isMove(from: number, to: number): boolean {
  return Math.abs(to - from) > 0.5;
}

/**
 * Where the centre logo comes to rest after riding a turn out to `deg`.
 *
 * The logo turns with the ring and then settles back to upright, and "upright" is any WHOLE number of turns,
 * not zero: after a few picks the ring can be three revolutions along, and unwinding all of them would be a
 * spin rather than a settle. Rounding to the nearest multiple of 360 keeps the way back to 180 degrees at
 * most, and — on the half of the moves where the logo is already past the half-turn — carries it FORWARD to
 * the next upright rather than dragging it back the way it came.
 */
export function uprightAfter(deg: number): number {
  return Math.round(deg / 360) * 360;
}
